import { useQuery } from "@tanstack/react-query";
import { auth } from "../../../components/Auth/Auth.jsx";

/*
 * Fetch the logs for the selected shop.
 *
 * Month and year are optional filters.
 */
const getShopLogs = async (shopId, month, year) => {
  const user = auth.currentUser;

  /*
   * Stop the request when there is no authenticated user.
   */
  if (!user) {
    throw new Error("No authenticated user found");
  }

  /*
   * A shop ID is required to request the shop logs.
   */
  if (!shopId) {
    throw new Error("Shop ID is required");
  }

  /*
   * Generate a Firebase authentication token.
   */
  const token = await user.getIdToken();

  /*
   * Convert the shop ID to the format expected by the backend.
   */
  const formattedShopId = String(shopId)
    .replace(/\./g, "/")
    .replace(/ /g, "+");

  /*
   * Build the request body sent to the Shop Logs API.
   *
   * The shop ID is always required.
   * Month and year are added only when they have valid values.
   */
  const requestBody = {
    shopId: formattedShopId,
    ...(month !== undefined &&
      month !== null &&
      month !== "" && { month: Number(month) }),
    ...(year !== undefined &&
      year !== null &&
      year !== "" && { year: Number(year) }),
  };
  

  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/api/v2/shop/shopLogs`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    },
  );

  /*
   * Handle unsuccessful API responses.
   */
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Unauthorized!");
    }

    throw new Error("Network response was not ok");
  }

  const responseData = await response.json();

  return responseData;
};

/*
 * React Query hook used to load the selected shop logs.
 */
export const UseShopLogsData = (shopId, month, year) => {
  const {
    data,
    isPending,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["ShopLogs", shopId, month, year],

    /*
     * Request the logs using the selected shop and date filters.
     */
    queryFn: () => getShopLogs(shopId, month, year),

    /*
     * Do not run the request until a shop has been selected.
     */
    enabled: Boolean(shopId),
  });

  return {
    data,
    isPending,
    isFetching,
    error,
    refetch,
  };
};